import { Link } from "react-router-dom";
import { BRAND, PACKAGES, WHY_CHOOSE, SERVICES } from "@/data/site";
import * as Lucide from "lucide-react";
import { ArrowRight, Check, Star, Sparkles } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import LiveStats from "@/components/LiveStats";
import RecentReviews from "@/components/RecentReviews";

const Icon = ({ name, ...p }) => { const C = Lucide[name] || Lucide.Leaf; return <C {...p}/>; };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center pt-24"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,8,6,0.55) 0%, rgba(5,8,6,0.78) 60%, #050806 100%), url(${BRAND.heroUrl})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#8DE038] bg-[#8DE038]/10 mb-6">
              <Sparkles size={14} className="text-[#8DE038]" />
              <span className="font-display uppercase tracking-[0.25em] text-xs text-[#8DE038]">Leeds · scaling nationally</span>
            </div>
            <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl lg:text-8xl text-[#F0F4F1] leading-[0.95]">
              Your garden. <br/>
              <span className="text-[#8DE038]">Their craft.</span> <br/>
              One platform.
            </h1>
            <p className="mt-7 max-w-xl text-lg text-[#C8D4CB] leading-relaxed">
              Green Connect matches you with vetted local pros in minutes — gardening, joinery, painting, property maintenance &amp; more. Upload photos, pick a date, pay a small platform fee — your pro takes care of the rest.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link data-testid="hero-book-btn" to="/book" className="btn-primary">Book a Pro <ArrowRight size={18}/></Link>
              <Link data-testid="hero-contractor-btn" to="/register?role=contractor" className="btn-secondary">Join as a Contractor</Link>
            </div>
            <div className="mt-10 flex items-center gap-6 flex-wrap text-sm text-[#9CAEA3] font-display uppercase tracking-widest">
              <span className="flex items-center gap-2"><Check size={16} className="text-[#8DE038]"/> Vetted pros</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-[#8DE038]"/> Pay-in-person</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-[#8DE038]"/> Rated &amp; reviewed</span>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:flex justify-end animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="w-full max-w-md">
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* OFFER STRIP */}
      <div data-testid="special-offer-strip" className="bg-[#8DE038] text-[#050806] py-5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="font-display uppercase tracking-[0.2em] text-base md:text-lg flex items-center gap-3">
            <Star size={20} fill="#050806"/> 10% OFF for OAPs, NHS Staff & First-Time Customers
          </div>
          <Link to="/book" className="font-display uppercase text-xs tracking-widest underline underline-offset-4">Claim your discount →</Link>
        </div>
      </div>

      {/* LIVE STATS TICKER */}
      <LiveStats />

      {/* WHY CHOOSE */}
      <section data-testid="why-choose-section" className="section-pad bg-[#050806]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ 01 Why Green Connect</div>
              <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">
                A marketplace built <br/> for your home &amp; garden.
              </h2>
            </div>
            <p className="max-w-md text-[#9CAEA3] leading-relaxed">We started in Leeds with one job in mind: connect homeowners with reliable, properly insured tradespeople — gardens, joinery, painting &amp; property fixes — without the faff of phone-tag.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[#1F3325]">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="border-r border-b border-[#1F3325] p-8 hover:bg-[#0F1712] transition-colors group">
                <div className="w-12 h-12 border border-[#8DE038]/50 flex items-center justify-center text-[#8DE038] mb-6 group-hover:bg-[#8DE038] group-hover:text-[#050806] transition-colors">
                  <Icon name={w.icon} size={22} />
                </div>
                <div className="font-display uppercase tracking-wider text-xl text-[#F0F4F1] mb-2">{w.title}</div>
                <p className="text-[#9CAEA3] leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (3 STEPS) */}
      <section data-testid="how-section" className="section-pad bg-[#0A0F0B]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="mb-12">
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ 02 How it works</div>
            <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1]">Three steps. No phone-tag.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Book a service", d: "Pick a service or package, upload photos of your garden, choose a date and pay a small platform fee." },
              { n: "02", t: "Get matched", d: "Your job is broadcast to vetted local pros. First to accept it locks in your booking." },
              { n: "03", t: "Pay in person", d: "Pro turns up, does the work, you pay them direct in cash or bank transfer. Then rate them." },
            ].map((s) => (
              <div key={s.n} className="gl-card p-8 md:p-10">
                <div className="font-display text-6xl text-[#8DE038] mb-4">{s.n}</div>
                <div className="font-display uppercase tracking-wider text-2xl text-[#F0F4F1]">{s.t}</div>
                <p className="text-[#9CAEA3] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-preview" className="section-pad bg-[#050806]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ 03 Services</div>
              <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">Complete<br/>home &amp; garden</h2>
            </div>
            <Link to="/services" className="btn-secondary text-xs">All Services <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {SERVICES.slice(0,6).map((s, i) => {
              const spans = ["md:col-span-7","md:col-span-5","md:col-span-4","md:col-span-4","md:col-span-4","md:col-span-12"];
              return (
                <Link key={s.slug} to="/services" className={`gl-card ${spans[i]} p-7 md:p-9 group block`}>
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 bg-[#8DE038]/10 border border-[#8DE038]/40 flex items-center justify-center text-[#8DE038]">
                      <Icon name={s.icon} size={22}/>
                    </div>
                    <ArrowRight className="text-[#1F3325] group-hover:text-[#8DE038] transition-colors" size={22}/>
                  </div>
                  <div className="font-display uppercase tracking-wider text-2xl md:text-3xl text-[#F0F4F1] leading-tight">{s.name}</div>
                  <p className="text-[#9CAEA3] mt-3 leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section data-testid="packages-section" className="section-pad bg-[#0A0F0B]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ 04 Popular Packages</div>
            <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1]">Choose your level of care</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PACKAGES.map((p) => (
              <div key={p.name} className={`relative p-8 md:p-10 border ${p.highlight ? "border-[#8DE038] bg-[#0F1712]" : "border-[#1F3325] bg-[#050806]"} flex flex-col`}>
                {p.highlight && (<div className="absolute -top-3 left-8 bg-[#8DE038] text-[#050806] font-display uppercase tracking-widest text-[10px] px-3 py-1">Most Popular</div>)}
                <div className="font-display uppercase tracking-widest text-sm text-[#9CAEA3]">{p.tagline}</div>
                <div className="font-display uppercase tracking-tighter text-5xl text-[#F0F4F1] mt-3">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl text-[#8DE038]">{p.price}</span>
                  <span className="text-[#9CAEA3] font-display uppercase tracking-widest text-xs">per visit</span>
                </div>
                <ul className="mt-7 space-y-3 text-[#F0F4F1]">
                  {p.features.map((f) => (<li key={f} className="flex items-start gap-3"><Check size={18} className="text-[#8DE038] mt-1 shrink-0"/><span>{f}</span></li>))}
                </ul>
                <Link to={`/book?package=${p.name.toLowerCase()}`} className={`mt-8 ${p.highlight ? "btn-primary" : "btn-secondary"} justify-center`}>Book {p.name}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <RecentReviews />
      <section data-testid="gallery-preview" className="section-pad bg-[#050806]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ 05 Recent Work</div>
              <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">Real gardens. Real pros.</h2>
            </div>
            <Link to="/gallery" className="btn-secondary text-xs">View Gallery <ArrowRight size={14}/></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {BRAND.gallery.slice(0,3).map((g) => (
              <div key={g.url} className="gl-card overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={g.url} alt={g.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                </div>
                <div className="p-5">
                  <div className="font-display uppercase tracking-wider text-lg text-[#F0F4F1]">{g.title}</div>
                  <div className="text-sm text-[#9CAEA3] mt-1">{g.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="section-pad relative" style={{ backgroundImage: `linear-gradient(rgba(5,8,6,0.78), rgba(5,8,6,0.85)), url(${BRAND.heroUrl})`, backgroundSize: "cover" }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1]">Ready to book a pro?</h2>
          <p className="mt-5 text-[#C8D4CB] text-lg">Free quotes, vetted contractors, transparent pricing.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-primary">Book Now <ArrowRight size={18}/></Link>
            <Link to="/register?role=contractor" className="btn-secondary">Become a Contractor</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
